import { Button } from '@components/Button';
import { IconButton } from '@components/IconButton';
import {
  IconChatBubbleOff,
  IconCheckCircle,
  IconDelete,
  IconDoNotDisturb,
  IconLoading,
  IconMessageRead,
  IconOpenInNew,
  IconRemoveCircle,
} from '@components/icons';
import { InfoChip } from '@components/InfoChip';
import { SegmentedControl } from '@components/SegmentedControl';
import styles from './ManageLeadsPage.module.scss';

export interface LeadActionBarProps {
  isNotOk: boolean;
  isOnboarded: boolean;
  isOnboarding: boolean;
  isOnboardDisabled: boolean;
  hasContactedAny: boolean;
  contactedCount: number;
  canDeleteLead: boolean;
  leadRating: 'neutral' | 'ok' | 'not-ok';
  leadStatus?: 'lost' | 'converted';
  onViewDemo: () => void;
  onOnboardSite: () => void;
  onDeleteLead: () => void;
  onRatingChange: (rating: string) => void;
}

export const LeadActionBar = ({
  isNotOk,
  isOnboarded,
  isOnboarding,
  isOnboardDisabled,
  hasContactedAny,
  contactedCount,
  canDeleteLead,
  leadRating,
  leadStatus,
  onViewDemo,
  onOnboardSite,
  onDeleteLead,
  onRatingChange,
}: LeadActionBarProps) => {
  const renderPrimaryAction = () => {
    if (leadStatus === 'lost') {
      return (
        <InfoChip
          label="Lost"
          type="danger"
          icon={<IconDoNotDisturb size={20} />}
        />
      );
    }
    if (leadStatus === 'converted') {
      return (
        <Button
          variant="primary"
          size="small"
          onClick={onViewDemo}
          disabled={isNotOk}
          trailingIcon={<IconOpenInNew size={18} />}
        >
          Converted
        </Button>
      );
    }
    if (isOnboarded) {
      return (
        <Button
          variant="primary"
          size="small"
          onClick={onViewDemo}
          disabled={isNotOk}
          trailingIcon={<IconOpenInNew size={18} />}
        >
          View Demo
        </Button>
      );
    }
    return (
      <Button
        variant="primary"
        size="small"
        onClick={onOnboardSite}
        disabled={isOnboardDisabled}
        leadingIcon={isOnboarding ? <IconLoading size={18} /> : undefined}
      >
        {isOnboarding ? 'Onboarding...' : 'Onboard Site'}
      </Button>
    );
  };

  return (
    <div className={styles.actionBar}>
      <div className={`${styles.actionBarLeft} ${isNotOk ? styles.disabled : ''}`}>
        {renderPrimaryAction()}
        {hasContactedAny ? (
          <InfoChip
            label={`Contacted (${contactedCount})`}
            type="primary"
            icon={<IconMessageRead size={20} />}
          />
        ) : (
          <InfoChip
            label="Not Contacted"
            type="warning"
            icon={<IconChatBubbleOff size={20} />}
          />
        )}
      </div>
      <div className={styles.actionBarRight}>
        {canDeleteLead && (
          <IconButton size="medium" aria-label="Delete Lead" onClick={onDeleteLead}>
            <IconDelete size={18} color="var(--color-semantic-danger)" />
          </IconButton>
        )}
        <SegmentedControl
          segments={[
            {
              value: 'not-ok',
              icon: <IconDoNotDisturb size={18} />,
              ariaLabel: 'Mark Not OK',
            },
            {
              value: 'neutral',
              icon: <IconRemoveCircle size={18} />,
              ariaLabel: 'Neutral',
            },
            {
              value: 'ok',
              icon: <IconCheckCircle size={18} />,
              ariaLabel: 'Mark OK',
            },
          ]}
          value={leadRating}
          onChange={onRatingChange}
        />
      </div>
    </div>
  );
};
